import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import {
  Channels,
  MAIL_DRIVER_LABELS,
  formatNumber,
  messages,
  type CampaignPreviewItem,
  type MailDriver,
} from '@nesc/shared';
import { api, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { StatusBadge } from '@/components/common/status-badge';
import { cn } from '@/lib/utils';

type PreviewResponse = {
  data: CampaignPreviewItem[];
  meta: { count: number; hasMore: boolean; nextCursor: string | null; filtered: number };
  summary: {
    total: number;
    sendable: number;
    noMessage: number;
    notApproved: number;
    noDestination: number;
    withNgPhrases: number;
    byChannel: { form: number; email: number };
  };
  sender: {
    driver: MailDriver;
    fromAddress: string;
    fromName: string;
    replyTo: string | null;
    configured: boolean;
    trackOpens: boolean;
    trackClicks: boolean;
    htmlMail: boolean;
    unsubscribeLink: boolean;
    safeMode: boolean;
    safeModeAllowlist: string[];
  };
};

const FILTERS = [
  { value: 'all', label: 'すべて' },
  { value: 'sendable', label: 'このまま送れるもの' },
  { value: 'blocked', label: '確認が必要なもの' },
] as const;

const PAGE_SIZE = 25;

/** S44 送信前プレビュー。実際に送る本文を全件確認する */
export const CampaignPreviewPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [only, setOnly] = useState<'all' | 'sendable' | 'blocked'>('all');
  const [channel, setChannel] = useState('');
  const [cursor, setCursor] = useState('0');
  const [selected, setSelected] = useState(0);

  const params = { only, channel: channel || undefined, cursor, limit: PAGE_SIZE };

  const { data, isLoading } = useQuery({
    queryKey: qk.campaignPreview(id, params),
    queryFn: () => api.get<PreviewResponse>(`/campaigns/${id}/preview`, params),
    placeholderData: (previous) => previous,
  });

  const { data: campaign } = useQuery({
    queryKey: qk.campaign(id),
    queryFn: () => api.get<SingleResponse<{ name: string }>>(`/campaigns/${id}`),
  });

  const resetTo = (next: Partial<{ only: typeof only; channel: string }>) => {
    if (next.only !== undefined) setOnly(next.only);
    if (next.channel !== undefined) setChannel(next.channel);
    setCursor('0');
    setSelected(0);
  };

  if (isLoading && !data) return <SkeletonRows rows={6} />;

  const items = data?.data ?? [];
  const current = items[Math.min(selected, items.length - 1)];
  const summary = data?.summary;
  const sender = data?.sender;
  const offset = Number(cursor) || 0;

  return (
    <>
      <PageHeader
        back={`/campaigns/${id}`}
        title={messages.campaign.previewTitle}
        description={
          <>
            {campaign?.data.name && <span className="mr-2">{campaign.data.name}</span>}
            {messages.campaign.previewNote}
          </>
        }
      />

      {sender && (
        <Card className="mb-4">
          <CardContent className="space-y-2 pt-4 lg:pt-5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body">
              <span>
                差出人：
                <span className="font-semibold">
                  {sender.fromName ? `${sender.fromName} <${sender.fromAddress}>` : sender.fromAddress || '未設定'}
                </span>
              </span>
              <span className="text-foreground-muted">{MAIL_DRIVER_LABELS[sender.driver]}</span>
              {sender.replyTo && <span className="text-foreground-muted">返信先 {sender.replyTo}</span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {sender.trackOpens && <Badge variant="dashed">開封を計測</Badge>}
              {sender.trackClicks && <Badge variant="dashed">クリックを計測</Badge>}
              {sender.unsubscribeLink && <Badge variant="dashed">配信停止リンクあり</Badge>}
              {!sender.htmlMail && <Badge variant="dashed">テキストのみ</Badge>}
            </div>
            {!sender.configured && (
              <p className="flex items-start gap-1.5 text-note font-semibold">
                <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
                メール送信の設定が未完了です。設定 → メール送信で差出人と API キーを登録してください。
              </p>
            )}
            {sender.safeMode && (
              <p className="text-note text-foreground-muted">
                セーフモード中です。許可ドメイン（{sender.safeModeAllowlist.join('、') || 'なし'}）以外へは実送信されません。
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {summary && (
        <Card className="mb-4">
          <CardContent className="pt-4 lg:pt-5">
            <p className="tabular flex flex-wrap gap-x-3 gap-y-1 text-body">
              <span>全 {formatNumber(summary.total)} 件</span>
              <span>送信可能 {formatNumber(summary.sendable)}</span>
              <span className={cn(summary.noMessage > 0 && 'font-semibold')}>
                文面なし {formatNumber(summary.noMessage)}
              </span>
              <span className={cn(summary.notApproved > 0 && 'font-semibold')}>
                未承認 {formatNumber(summary.notApproved)}
              </span>
              <span className={cn(summary.withNgPhrases > 0 && 'font-semibold')}>
                禁止表現 {formatNumber(summary.withNgPhrases)}
              </span>
              <span>
                メール {formatNumber(summary.byChannel.email)}／フォーム{' '}
                {formatNumber(summary.byChannel.form)}
              </span>
            </p>
          </CardContent>
        </Card>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={only === filter.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => resetTo({ only: filter.value })}
          >
            {filter.label}
          </Button>
        ))}
        <Select
          className="w-auto"
          value={channel}
          onChange={(e) => resetTo({ channel: e.target.value })}
          aria-label="チャネルで絞り込む"
        >
          <option value="">チャネル：すべて</option>
          {Channels.list.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
      </div>

      {items.length === 0 ? (
        <EmptyState title={messages.campaign.previewEmpty} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
          <Card className="order-2 lg:order-1">
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {items.map((item, index) => (
                  <li key={item.companyId}>
                    <button
                      type="button"
                      onClick={() => setSelected(index)}
                      className={cn(
                        'w-full space-y-1 p-3 text-left hover:bg-muted',
                        index === selected && 'bg-muted',
                      )}
                    >
                      <span className="block truncate text-body font-semibold">
                        {offset + index + 1}. {item.companyName}
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {item.channel && <StatusBadge kind="channel" value={item.channel} />}
                        {item.messageStatus && (
                          <StatusBadge kind="message_status" value={item.messageStatus} />
                        )}
                        {item.warnings.length > 0 && (
                          <span className="rounded-control border-2 border-foreground px-1.5 text-note font-semibold">
                            要確認
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <div className="order-1 space-y-3 lg:order-2">
            {current && <PreviewPane item={current} onEdit={(messageId) => navigate(`/messages/${messageId}`)} />}

            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={selected === 0 && offset === 0}
                onClick={() => {
                  if (selected > 0) setSelected(selected - 1);
                  else if (offset > 0) {
                    setCursor(String(Math.max(offset - PAGE_SIZE, 0)));
                    setSelected(PAGE_SIZE - 1);
                  }
                }}
              >
                <ChevronLeft aria-hidden />
                前の 1 件
              </Button>
              <span className="tabular text-note text-foreground-muted">
                {offset + selected + 1} / {formatNumber(data?.meta.filtered ?? 0)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={selected >= items.length - 1 && !data?.meta.hasMore}
                onClick={() => {
                  if (selected < items.length - 1) setSelected(selected + 1);
                  else if (data?.meta.nextCursor) {
                    setCursor(data.meta.nextCursor);
                    setSelected(0);
                  }
                }}
              >
                次の 1 件
                <ChevronRight aria-hidden />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const PreviewPane = ({
  item,
  onEdit,
}: {
  item: CampaignPreviewItem;
  onEdit: (messageId: string) => void;
}) => (
  <Card>
    <CardContent className="space-y-3 pt-4 lg:pt-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-body font-semibold">{item.companyName}</p>
          <p className="break-all text-note text-foreground-muted">
            {item.channel === 'form' ? 'フォーム' : '宛先'}：{item.destination ?? '—'}
          </p>
        </div>
        {item.messageId && (
          <Button variant="outline" size="sm" onClick={() => onEdit(item.messageId!)}>
            <Pencil aria-hidden />
            {messages.common.edit}
          </Button>
        )}
      </div>

      {item.warnings.length > 0 && (
        <ul className="space-y-1 rounded-control border-2 border-foreground p-3">
          {item.warnings.map((warning) => (
            <li key={warning} className="flex items-start gap-1.5 text-note font-semibold">
              <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
              <span>{warning}</span>
            </li>
          ))}
        </ul>
      )}

      {item.body ? (
        <div className="rounded-control border border-border bg-muted p-3">
          {item.channel !== 'form' && (
            <p className="mb-2 border-b border-border pb-2 text-body font-semibold">
              件名：{item.subject ?? '（件名なし）'}
            </p>
          )}
          <pre className="whitespace-pre-wrap break-words text-body">{item.body}</pre>
        </div>
      ) : (
        <p className="text-body text-foreground-muted">{messages.campaign.previewEmpty}</p>
      )}
    </CardContent>
  </Card>
);
